/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { meclisBaskaniAPI, meetingsAPI, announcementsAPI, ticketsAPI, workingGroupsAPI } from '../../lib/api';
import { formatDate, formatDateTime, formatRelativeTime, TICKET_STATUS, TICKET_TYPES, TICKET_PRIORITY, MEETING_TYPES, TARGET_AUDIENCES, formatApiErrorDetail } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Calendar, Megaphone, BarChart3,
  Plus, Eye, Clock, CheckCircle, AlertCircle, TrendingUp,
  Loader2, UsersRound, Shield, ArrowRight, Forward, Search,
  ChevronLeft, ChevronRight, Filter, Download,
} from 'lucide-react';

// ============ STAT CARD ============
const StatCard = ({ title, value, icon: Icon, color = 'blue', sub }) => {
  const cls = {
    blue: 'bg-blue-50 text-blue-600 ring-blue-100',
    green: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    yellow: 'bg-amber-50 text-amber-600 ring-amber-100',
    red: 'bg-red-50 text-red-600 ring-red-100',
    purple: 'bg-purple-50 text-purple-600 ring-purple-100',
    cyan: 'bg-cyan-50 text-cyan-600 ring-cyan-100',
  };
  return (
    <Card className="group hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`stat-${title.replace(/\s/g, '-').toLowerCase()}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-extrabold text-slate-900 mt-1 tracking-tight">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1.5">{sub}</p>}
          </div>
          <div className={`p-3 rounded-xl ring-1 ${cls[color]} group-hover:scale-110 transition-transform duration-300`}><Icon className="w-5 h-5" /></div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ HOME TAB ============
const HomeTab = ({ data, loading }) => {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  if (!data) return null;
  const { stats, meclis, upcoming_meetings, recent_announcements, recent_tickets } = data;
  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-800 rounded-2xl p-6 text-white" data-testid="meclis-header">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px), radial-gradient(circle at 75% 25%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative">
          <h2 className="text-2xl font-bold tracking-tight">{meclis?.name}</h2>
          <p className="text-blue-200 mt-1">{meclis?.description || 'Meclis Baskani Paneli'}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Calisma Gruplari" value={stats.working_groups} icon={UsersRound} color="purple" />
        <StatCard title="Toplam Uye" value={stats.members} icon={Users} color="blue" sub={stats.pending_applications > 0 ? `${stats.pending_applications} bekleyen basvuru` : null} />
        <StatCard title="Vatandas Talepleri" value={stats.total_tickets} icon={FileText} color="yellow" sub={`${stats.open_tickets} acik`} />
        <StatCard title="Cozum Orani" value={`%${stats.resolution_rate}`} icon={TrendingUp} color="green" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplantilar" value={stats.total_meetings} icon={Calendar} color="cyan" />
        <StatCard title="Duyurular" value={stats.total_announcements} icon={Megaphone} color="red" />
        <StatCard title="Cozulen Talepler" value={stats.resolved_tickets} icon={CheckCircle} color="green" />
        <StatCard title="Acik Talepler" value={stats.open_tickets} icon={AlertCircle} color="yellow" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Meetings */}
        <Card className="card-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Yaklasan Toplantilar</CardTitle>
              <Calendar className="w-5 h-5 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent>
            {upcoming_meetings?.length > 0 ? upcoming_meetings.map(m => (
              <div key={m.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div>
                  <p className="font-medium text-sm text-slate-900">{m.title}</p>
                  <p className="text-xs text-slate-500">{formatDate(m.date)} - {m.time}</p>
                </div>
                <Badge className="text-xs" variant="outline">{MEETING_TYPES[m.meeting_type]?.label || m.meeting_type}</Badge>
              </div>
            )) : <p className="text-sm text-slate-400 py-4 text-center">Yaklasan toplanti yok</p>}
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card className="card-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Son Talepler</CardTitle>
              <FileText className="w-5 h-5 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent>
            {recent_tickets?.length > 0 ? recent_tickets.map(t => (
              <div key={t.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-slate-900 truncate">{t.subject}</p>
                  <p className="text-xs text-slate-500">{t.ticket_no} - {formatRelativeTime(t.created_at)}</p>
                </div>
                <Badge className="text-xs ml-2 shrink-0">{TICKET_STATUS[t.status]?.label || t.status}</Badge>
              </div>
            )) : <p className="text-sm text-slate-400 py-4 text-center">Henuz talep yok</p>}
          </CardContent>
        </Card>
      </div>

      {/* Recent Announcements */}
      {recent_announcements?.length > 0 && (
        <Card className="card-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Son Duyurular</CardTitle>
              <Megaphone className="w-5 h-5 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent>
            {recent_announcements.map(a => (
              <div key={a.id} className="py-3 border-b last:border-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm text-slate-900">{a.title}</p>
                  <Badge variant="outline" className="text-xs">{a.priority === 'yuksek' ? 'Yuksek' : a.priority === 'acil' ? 'Acil' : 'Normal'}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">{formatRelativeTime(a.created_at)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Anketler Quick Access */}
      <Card className="card-shadow border-violet-200 bg-violet-50/30" data-testid="mb-surveys-bar">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Anketler</h3>
              <p className="text-sm text-slate-600">Meclis anketlerini yonetin ve sonuclarini inceleyin</p>
            </div>
            <Link to="/panel/anketler">
              <Button className="bg-violet-600 hover:bg-violet-700" data-testid="mb-surveys-nav-btn">
                Anketlere Git <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ WORKING GROUPS TAB ============
const WorkingGroupsTab = ({ meclis }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  // Member management
  const [selectedWG, setSelectedWG] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberFilter, setMemberFilter] = useState('');

  const filteredMembers = members.filter(m => {
    if (!memberFilter) return true;
    const q = memberFilter.toLowerCase();
    return (m.name + ' ' + m.surname).toLowerCase().includes(q) ||
           (m.user_email || '').toLowerCase().includes(q) ||
           (m.phone || '').includes(q) ||
           (m.ilce_name || '').toLowerCase().includes(q) ||
           (m.mahalle_name || '').toLowerCase().includes(q);
  });
  const [showAddMember, setShowAddMember] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Applications
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [showApps, setShowApps] = useState(false);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try { const r = await meclisBaskaniAPI.getWorkingGroups(); setGroups(r.data); }
    catch { toast.error('Calisma gruplari yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const handleCreateWG = async () => {
    if (!createForm.name.trim()) { toast.error('Grup adi zorunludur'); return; }
    setCreating(true);
    try {
      await meclisBaskaniAPI.createWorkingGroup(createForm);
      toast.success('Calisma grubu olusturuldu');
      setShowCreate(false);
      setCreateForm({ name: '', description: '' });
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    finally { setCreating(false); }
  };

  const loadMembers = async (wg) => {
    setSelectedWG(wg);
    setMembersLoading(true);
    try { const r = await meclisBaskaniAPI.getWGMembers(wg.id); setMembers(r.data); }
    catch { toast.error('Uyeler yuklenemedi'); }
    finally { setMembersLoading(false); }
  };

  const searchUsers = async (q) => {
    setUserSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try { const r = await meclisBaskaniAPI.searchUsers(q); setSearchResults(r.data); }
    catch (e) { console.error("Load error:", e); }
    finally { setSearching(false); }
  };

  const addMember = async (userId) => {
    try {
      await meclisBaskaniAPI.addWGMember(selectedWG.id, userId);
      toast.success('Uye eklendi');
      loadMembers(selectedWG);
      setShowAddMember(false);
      setUserSearch('');
      setSearchResults([]);
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eklenemedi'); }
  };

  const removeMember = async (userId) => {
    try {
      await meclisBaskaniAPI.removeWGMember(selectedWG.id, userId);
      toast.success('Uye cikarildi');
      loadMembers(selectedWG);
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cikarilamadi'); }
  };

  const loadApplications = async () => {
    setAppsLoading(true);
    setShowApps(true);
    try { const r = await meclisBaskaniAPI.getApplications({ status: 'beklemede' }); setApplications(r.data); }
    catch { toast.error('Basvurular yuklenemedi'); }
    finally { setAppsLoading(false); }
  };

  const handleAppAction = async (appId, action) => {
    try {
      if (action === 'approve') await workingGroupsAPI.approve(appId);
      else await workingGroupsAPI.reject(appId);
      toast.success(action === 'approve' ? 'Basvuru onaylandi' : 'Basvuru reddedildi');
      loadApplications();
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Islem basarisiz'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  return (
    <div className="space-y-4" data-testid="working-groups-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-slate-900">{meclis?.name} - Calisma Gruplari ({groups.length})</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadApplications} data-testid="view-applications-btn">
            <Clock className="w-4 h-4 mr-1" /> Basvurular
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-wg-btn">
            <Plus className="w-4 h-4 mr-1" /> Calisma Grubu Olustur
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">Bu meclise ait calisma grubu bulunamadi</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(g => (
            <Card key={g.id} className="group hover:shadow-lg transition-all duration-300" data-testid={`wg-card-${g.id}`}>
              <CardContent className="p-5">
                <h4 className="font-semibold text-slate-900 mb-2">{g.name}</h4>
                {g.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{g.description}</p>}
                <div className="flex items-center gap-4 text-sm mb-3">
                  <div className="flex items-center gap-1 text-blue-600"><Users className="w-4 h-4" /><span>{g.member_count} uye</span></div>
                  {g.pending_count > 0 && (
                    <div className="flex items-center gap-1 text-amber-600"><Clock className="w-4 h-4" /><span>{g.pending_count} bekleyen</span></div>
                  )}
                </div>
                <Button size="sm" variant="outline" className="w-full" onClick={() => loadMembers(g)} data-testid={`manage-members-${g.id}`}>
                  <Users className="w-4 h-4 mr-1" /> Uyeleri Yonet
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create WG Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Calisma Grubu</DialogTitle>
            <DialogDescription>{meclis?.name} altinda yeni calisma grubu olusturun</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Grup Adi *</Label><Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="orn: Cevre Calisma Grubu" data-testid="wg-name-input" /></div>
            <div><Label>Aciklama</Label><Textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Grubun amaci ve calisma alani" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Iptal</Button>
            <Button onClick={handleCreateWG} disabled={creating} data-testid="submit-wg-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Olustur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Management Dialog - Zenginlestirilmis */}
      <Dialog open={!!selectedWG} onOpenChange={() => { setSelectedWG(null); setMemberDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-0">
            <DialogTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              {selectedWG?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">{members.length} onaylanmis uye</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 mt-3">
            {/* Arama + Ekle */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input placeholder="Uye ara..." value={memberFilter} onChange={e => setMemberFilter(e.target.value)} className="h-8 text-xs pl-8" data-testid="member-filter" />
              </div>
              {members.length > 0 && (
                <a href={meclisBaskaniAPI.exportWGMembers(selectedWG?.id)} className="inline-flex items-center justify-center h-8 px-3 text-xs font-medium rounded-md border border-slate-200 bg-white text-emerald-700 hover:bg-emerald-50 transition-colors" data-testid="export-members-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Excel
                </a>
              )}
              <Button size="sm" className="h-8 text-xs" onClick={() => setShowAddMember(true)} data-testid="add-member-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Uye Ekle
              </Button>
            </div>

            {membersLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : members.length === 0 ? (
              <div className="text-center py-8"><Users className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Henuz uye yok</p></div>
            ) : (
              <>
                {/* Tablo */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead className="text-[10px] font-semibold">Ad Soyad</TableHead>
                        <TableHead className="text-[10px] font-semibold">Iletisim</TableHead>
                        <TableHead className="text-[10px] font-semibold">Ilce / Mahalle</TableHead>
                        <TableHead className="text-[10px] font-semibold">Basvuru</TableHead>
                        <TableHead className="text-[10px] font-semibold w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.map(m => (
                        <TableRow key={m.id} className="group cursor-pointer hover:bg-blue-50/30" onClick={() => setMemberDetail(m)} data-testid={`member-row-${m.user_id}`}>
                          <TableCell className="py-2.5">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-700 text-[10px] font-bold flex-shrink-0">
                                {m.name?.[0]}{m.surname?.[0]}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-900 truncate">{m.name} {m.surname}</p>
                                <p className="text-[10px] text-slate-400">{m.cinsiyet === 'erkek' ? 'Erkek' : m.cinsiyet === 'kadin' ? 'Kadin' : ''}{m.dogum_tarihi ? ` · ${m.dogum_tarihi}` : ''}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-2.5">
                            <p className="text-[11px] text-slate-700">{m.user_email}</p>
                            {m.phone && <p className="text-[10px] text-slate-500">{m.phone}</p>}
                          </TableCell>
                          <TableCell className="py-2.5">
                            <p className="text-[11px] text-slate-700">{m.ilce_name || '-'}</p>
                            {m.mahalle_name && <p className="text-[10px] text-slate-400">{m.mahalle_name}</p>}
                          </TableCell>
                          <TableCell className="py-2.5">
                            <p className="text-[10px] text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString('tr-TR') : '-'}</p>
                          </TableCell>
                          <TableCell className="py-2.5">
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-500" onClick={(e) => { e.stopPropagation(); setMemberDetail(m); }}><Eye className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeMember(m.user_id); }} data-testid={`remove-member-${m.user_id}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Member Detail Dialog */}
      <Dialog open={!!memberDetail} onOpenChange={() => setMemberDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Uye Detayi</DialogTitle>
          </DialogHeader>
          {memberDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center text-blue-700 text-sm font-bold">
                  {memberDetail.name?.[0]}{memberDetail.surname?.[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{memberDetail.name} {memberDetail.surname}</p>
                  <p className="text-xs text-slate-500">{memberDetail.user_email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-slate-400 block text-[10px] uppercase">Telefon</span><span className="text-slate-800 font-medium">{memberDetail.phone || '-'}</span></div>
                <div><span className="text-slate-400 block text-[10px] uppercase">TC Kimlik</span><span className="text-slate-800 font-medium">{memberDetail.tc_kimlik || '-'}</span></div>
                <div><span className="text-slate-400 block text-[10px] uppercase">Cinsiyet</span><span className="text-slate-800 font-medium">{memberDetail.cinsiyet === 'erkek' ? 'Erkek' : memberDetail.cinsiyet === 'kadin' ? 'Kadin' : '-'}</span></div>
                <div><span className="text-slate-400 block text-[10px] uppercase">Dogum Tarihi</span><span className="text-slate-800 font-medium">{memberDetail.dogum_tarihi || '-'}</span></div>
                <div><span className="text-slate-400 block text-[10px] uppercase">Ilce</span><span className="text-slate-800 font-medium">{memberDetail.ilce_name || '-'}</span></div>
                <div><span className="text-slate-400 block text-[10px] uppercase">Mahalle</span><span className="text-slate-800 font-medium">{memberDetail.mahalle_name || '-'}</span></div>
              </div>
              {memberDetail.acik_adres && (
                <div className="text-xs"><span className="text-slate-400 block text-[10px] uppercase">Adres</span><span className="text-slate-800">{memberDetail.acik_adres}</span></div>
              )}
              {memberDetail.motivation && (
                <div className="text-xs border-t pt-3"><span className="text-slate-400 block text-[10px] uppercase mb-1">Basvuru Motivasyonu</span><p className="text-slate-700 bg-slate-50 p-2.5 rounded-lg">{memberDetail.motivation}</p></div>
              )}
              {memberDetail.experience && (
                <div className="text-xs"><span className="text-slate-400 block text-[10px] uppercase mb-1">Deneyim</span><p className="text-slate-700 bg-slate-50 p-2.5 rounded-lg">{memberDetail.experience}</p></div>
              )}
              {memberDetail.skills && memberDetail.skills.length > 0 && (
                <div className="text-xs"><span className="text-slate-400 block text-[10px] uppercase mb-1">Yetenekler</span>
                  <div className="flex flex-wrap gap-1">{memberDetail.skills.map((s, i) => <Badge key={`skill-${i}`} variant="outline" className="text-[10px]">{s}</Badge>)}</div>
                </div>
              )}
              <div className="text-xs text-slate-400 pt-2 border-t">Basvuru: {memberDetail.created_at ? new Date(memberDetail.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uye Ekle</DialogTitle>
            <DialogDescription>Kullanici arayip gruba ekleyin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Isim, soyisim veya e-posta ile arama..." value={userSearch} onChange={e => searchUsers(e.target.value)} data-testid="user-search-input" />
            {searching && <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>}
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {searchResults.map(u => (
                <div key={u.id} className="flex items-center justify-between p-2.5 hover:bg-slate-50 rounded-lg cursor-pointer" data-testid={`search-user-${u.id}`}>
                  <div>
                    <p className="text-sm font-medium">{u.name} {u.surname}</p>
                    <p className="text-xs text-slate-500">{u.email} ({u.role})</p>
                  </div>
                  <Button size="sm" onClick={() => addMember(u.id)} data-testid={`add-user-${u.id}`}><Plus className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Applications Dialog */}
      <Dialog open={showApps} onOpenChange={setShowApps}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bekleyen Basvurular</DialogTitle>
            <DialogDescription>Calisma grubu basvurularini yonetin</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {appsLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : applications.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Bekleyen basvuru yok</p>
            ) : (
              applications.map(app => (
                <div key={app.id} className="p-3 border rounded-lg" data-testid={`app-${app.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">{app.user_name}</p>
                      <p className="text-xs text-slate-500">{app.user_email} - {app.organ_name}</p>
                      {app.motivation && <p className="text-xs text-slate-600 mt-1">{app.motivation}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0 ml-2">
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => handleAppAction(app.id, 'approve')} data-testid={`approve-app-${app.id}`}>
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleAppAction(app.id, 'reject')} data-testid={`reject-app-${app.id}`}>
                        <AlertCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ TICKETS TAB ============
const TicketsTab = ({ meclis }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [forwardDialog, setForwardDialog] = useState(null);
  const [returnDialog, setReturnDialog] = useState(null);
  const [organs, setOrgans] = useState([]);
  const [forwardTarget, setForwardTarget] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [returnNote, setReturnNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const limit = 15;

  useEffect(() => { loadTickets(); }, [page, statusFilter, categoryFilter]);
  useEffect(() => {
    ticketsAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
    loadForwardTargets();
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * limit, limit, status: statusFilter || undefined, category: categoryFilter || undefined, search: search || undefined };
      const r = await meclisBaskaniAPI.getTickets(params);
      setTickets(r.data.tickets); setTotal(r.data.total);
    } catch { toast.error('Talepler yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const loadForwardTargets = async () => {
    try {
      const r = await workingGroupsAPI.getMeclisler();
      const allOrgans = [];
      for (const m of r.data) {
        allOrgans.push({ id: m.id, name: m.name, type: 'meclis' });
        if (m.calisma_gruplari) {
          for (const cg of m.calisma_gruplari) {
            allOrgans.push({ id: cg.id, name: `${m.name} > ${cg.name}`, type: 'calisma_grubu' });
          }
        }
      }
      setOrgans(allOrgans);
    } catch (e) { console.error("Forward targets error:", e); }
  };

  const handleSearch = () => { setPage(1); loadTickets(); };

  const handleForward = async () => {
    if (!forwardTarget) { toast.error('Hedef organ secin'); return; }
    setActionLoading(true);
    try {
      await ticketsAPI.forward(forwardDialog.id, {
        assigned_organ_id: forwardTarget,
        note: forwardNote || undefined,
      });
      toast.success('Talep basariyla yonlendirildi');
      setForwardDialog(null);
      setForwardTarget('');
      setForwardNote('');
      loadTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yonlendirme basarisiz');
    } finally { setActionLoading(false); }
  };

  const handleReturn = async () => {
    setActionLoading(true);
    try {
      await ticketsAPI.returnTicket(returnDialog.id, { note: returnNote || undefined });
      toast.success('Talep basariyla iade edildi');
      setReturnDialog(null);
      setReturnNote('');
      loadTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Iade basarisiz');
    } finally { setActionLoading(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const getStatusBadge = (status) => {
    const s = TICKET_STATUS[status] || { label: status, color: 'default' };
    const cls = { default: 'bg-slate-100 text-slate-700', info: 'bg-blue-50 text-blue-700', warning: 'bg-amber-50 text-amber-700', success: 'bg-emerald-50 text-emerald-700', error: 'bg-red-50 text-red-700' };
    return <Badge className={cls[s.color] || cls.default}>{s.label}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="tickets-tab">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Ara (ID, konu)..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="pl-9" data-testid="mb-ticket-search" />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="mb-ticket-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tumu</SelectItem>
            {Object.entries(TICKET_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="mb-ticket-cat-filter"><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tumu</SelectItem>
            {categories.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleSearch}><Filter className="w-4 h-4 mr-1" /> Filtrele</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : tickets.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">Bu meclise yonlendirilmis talep bulunamadi</CardContent></Card>
      ) : (
        <>
          <Card className="card-shadow overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs">No</TableHead>
                  <TableHead className="text-xs">Konu</TableHead>
                  <TableHead className="text-xs">Atanan Organ</TableHead>
                  <TableHead className="text-xs">Durum</TableHead>
                  <TableHead className="text-xs">Yonlendirme</TableHead>
                  <TableHead className="text-xs">Tarih</TableHead>
                  <TableHead className="text-xs text-right">Islemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map(t => (
                  <TableRow key={t.id} className="hover:bg-slate-50/50" data-testid={`mb-ticket-row-${t.id}`}>
                    <TableCell className="font-mono text-xs text-blue-600 font-medium">{t.ticket_no}</TableCell>
                    <TableCell className="max-w-[250px]">
                      <p className="font-medium text-sm truncate">{t.subject}</p>
                      <p className="text-xs text-slate-400 truncate">{t.created_by_name}</p>
                    </TableCell>
                    <TableCell>
                      {t.assigned_organ_name ? (
                        <Badge variant="outline" className={`text-xs ${t.assigned_organ_type === 'meclis' ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'}`}>
                          {t.assigned_organ_name}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Atanmamis</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(t.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {[...Array(3)].map((_, i) => (
                          <div key={`fw-${i}`} className={`w-2 h-2 rounded-full ${i < (t.forward_count || 0) ? 'bg-amber-500' : 'bg-slate-200'}`} />
                        ))}
                        <span className="text-xs text-slate-500 ml-1">{t.forward_count || 0}/3</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{formatRelativeTime(t.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => navigate(`/panel/talepler/${t.id}`)} data-testid={`mb-ticket-view-${t.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          disabled={(t.forward_count || 0) >= 3}
                          onClick={() => { setForwardDialog(t); setForwardTarget(''); setForwardNote(''); }}
                          data-testid={`mb-ticket-forward-${t.id}`}
                        >
                          <Forward className="w-4 h-4" />
                        </Button>
                        {t.assigned_organ_id && (
                          <Button
                            size="sm" variant="ghost" className="h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => { setReturnDialog(t); setReturnNote(''); }}
                            data-testid={`mb-ticket-return-${t.id}`}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Toplam {total} talep</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm">{page} / {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Yonlendirme Dialog */}
      <Dialog open={!!forwardDialog} onOpenChange={() => setForwardDialog(null)}>
        <DialogContent className="max-w-md" data-testid="forward-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Forward className="w-5 h-5 text-blue-600" />
              Talebi Yonlendir
            </DialogTitle>
            <DialogDescription>
              {forwardDialog?.ticket_no} - {forwardDialog?.subject}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {forwardDialog && (forwardDialog.forward_count || 0) > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm text-amber-800">
                  Bu talep daha once <strong>{forwardDialog.forward_count}</strong> kez yonlendirildi.
                  Kalan hak: <strong>{3 - (forwardDialog.forward_count || 0)}</strong>
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Hedef Organ *</Label>
              <Select value={forwardTarget} onValueChange={setForwardTarget}>
                <SelectTrigger data-testid="forward-target-select"><SelectValue placeholder="Meclis veya Calisma Grubu secin" /></SelectTrigger>
                <SelectContent>
                  {organs.filter(o => o.id !== forwardDialog?.assigned_organ_id).map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${o.type === 'meclis' ? 'border-blue-300' : 'border-emerald-300'}`}>
                          {o.type === 'meclis' ? 'M' : 'CG'}
                        </Badge>
                        {o.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Not (opsiyonel)</Label>
              <Textarea value={forwardNote} onChange={e => setForwardNote(e.target.value)} placeholder="Yonlendirme sebebini yazin..." rows={3} data-testid="forward-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForwardDialog(null)}>Iptal</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleForward} disabled={actionLoading || !forwardTarget} data-testid="forward-confirm-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Forward className="w-4 h-4 mr-2" />}
              Yonlendir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iade Dialog */}
      <Dialog open={!!returnDialog} onOpenChange={() => setReturnDialog(null)}>
        <DialogContent className="max-w-md" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChevronLeft className="w-5 h-5 text-amber-600" />
              Talebi Iade Et
            </DialogTitle>
            <DialogDescription>
              {returnDialog?.ticket_no} - {returnDialog?.subject}
              {returnDialog?.assigned_organ_name && (
                <span className="block mt-1 text-amber-700">Mevcut atama: <strong>{returnDialog.assigned_organ_name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-800">
                Bu talep mevcut calisma grubundan iade edilecek ve ust meclis'e geri donecektir.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Iade Notu (opsiyonel)</Label>
              <Textarea value={returnNote} onChange={e => setReturnNote(e.target.value)} placeholder="Iade sebebini yazin..." rows={3} data-testid="return-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialog(null)}>Iptal</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleReturn} disabled={actionLoading} data-testid="return-confirm-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ChevronLeft className="w-4 h-4 mr-2" />}
              Iade Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ MEETINGS TAB ============
const MeetingsTab = ({ meclis }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', location: '', meeting_type: 'meclis', target_audience: ['meclisler'], agenda: [] });
  const [agendaItem, setAgendaItem] = useState('');

  useEffect(() => { loadMeetings(); }, []);
  const loadMeetings = useCallback(async () => {
    setLoading(true);
    try { const r = await meetingsAPI.getAll({ limit: 100 }); setMeetings(r.data.meetings); }
    catch { toast.error('Toplantilar yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const handleCreate = async () => {
    if (!form.title || !form.date || !form.time) { toast.error('Baslik, tarih ve saat zorunludur'); return; }
    setCreating(true);
    try {
      await meetingsAPI.create(form);
      toast.success('Toplanti olusturuldu');
      setShowCreate(false);
      setForm({ title: '', description: '', date: '', time: '', location: '', meeting_type: 'meclis', target_audience: ['meclisler'], agenda: [] });
      loadMeetings();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Toplanti olusturulamadi'); }
    finally { setCreating(false); }
  };

  const addAgenda = () => {
    if (agendaItem.trim()) { setForm(f => ({ ...f, agenda: [...f.agenda, agendaItem.trim()] })); setAgendaItem(''); }
  };

  return (
    <div className="space-y-4" data-testid="meetings-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Toplantilar</h3>
        <Button onClick={() => setShowCreate(true)} data-testid="create-meeting-btn"><Plus className="w-4 h-4 mr-2" /> Toplanti Olustur</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : meetings.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">Henuz toplanti olusturulmamis</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {meetings.map(m => (
            <Card key={m.id} className="card-shadow" data-testid={`meeting-card-${m.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-slate-900">{m.title}</h4>
                  <Badge className="text-xs" variant="outline">{MEETING_TYPES[m.meeting_type]?.label || m.meeting_type}</Badge>
                </div>
                {m.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{m.description}</p>}
                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(m.date)}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{m.time}</span>
                  {m.location && <span className="flex items-center gap-1 text-blue-600">{m.location}</span>}
                </div>
                {m.agenda?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium text-slate-600 mb-1">Gundem:</p>
                    <ul className="text-xs text-slate-500 space-y-0.5">
                      {m.agenda.slice(0, 3).map((a, i) => <li key={`agenda-${i}-${a.slice(0,10)}`}>- {a}</li>)}
                      {m.agenda.length > 3 && <li className="text-slate-400">+{m.agenda.length - 3} daha...</li>}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Meeting Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Yeni Toplanti</DialogTitle>
            <DialogDescription>{meclis?.name} icin yeni toplanti olusturun</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div><Label>Baslik *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Toplanti basligi" data-testid="meeting-title-input" /></div>
            <div><Label>Aciklama</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Toplanti aciklamasi" rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tarih *</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="meeting-date-input" /></div>
              <div><Label>Saat *</Label><Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} data-testid="meeting-time-input" /></div>
            </div>
            <div><Label>Yer / Online Link</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Toplanti salonu veya Zoom/Teams linki" /></div>
            <div><Label>Toplanti Turu</Label>
              <Select value={form.meeting_type} onValueChange={v => setForm(f => ({ ...f, meeting_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MEETING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gundem Maddeleri</Label>
              <div className="flex gap-2 mt-1">
                <Input value={agendaItem} onChange={e => setAgendaItem(e.target.value)} placeholder="Gundem maddesi ekle" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAgenda())} />
                <Button type="button" variant="outline" size="sm" onClick={addAgenda}><Plus className="w-4 h-4" /></Button>
              </div>
              {form.agenda.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {form.agenda.map((a, i) => (
                    <li key={`agenda-item-${i}-${a.slice(0,8)}`} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-sm">
                      <span>{i + 1}. {a}</span>
                      <button onClick={() => setForm(f => ({ ...f, agenda: f.agenda.filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600 text-xs">Sil</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-meeting-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />} Olustur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ ANNOUNCEMENTS TAB ============
const AnnouncementsTab = ({ meclis }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', target_audience: ['meclisler'], priority: 'normal', expires_at: '' });

  useEffect(() => { loadAnnouncements(); }, []);
  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try { const r = await announcementsAPI.getAll({ limit: 100 }); setAnnouncements(r.data.announcements); }
    catch { toast.error('Duyurular yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  const handleCreate = async () => {
    if (!form.title || !form.content) { toast.error('Baslik ve icerik zorunludur'); return; }
    setCreating(true);
    try {
      const payload = { ...form, expires_at: form.expires_at || undefined };
      await announcementsAPI.create(payload);
      toast.success('Duyuru yayinlandi');
      setShowCreate(false);
      setForm({ title: '', content: '', target_audience: ['meclisler'], priority: 'normal', expires_at: '' });
      loadAnnouncements();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Duyuru olusturulamadi'); }
    finally { setCreating(false); }
  };

  const priorityCls = { normal: 'bg-slate-100 text-slate-700', yuksek: 'bg-amber-50 text-amber-700', acil: 'bg-red-50 text-red-700' };

  return (
    <div className="space-y-4" data-testid="announcements-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Duyurular</h3>
        <Button onClick={() => setShowCreate(true)} data-testid="create-announcement-btn"><Plus className="w-4 h-4 mr-2" /> Duyuru Yayinla</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : announcements.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">Henuz duyuru yayinlanmamis</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {announcements.map(a => (
            <Card key={a.id} className="card-shadow" data-testid={`announcement-card-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-slate-900">{a.title}</h4>
                  <Badge className={`text-xs ${priorityCls[a.priority] || priorityCls.normal}`}>{a.priority === 'yuksek' ? 'Yuksek' : a.priority === 'acil' ? 'Acil' : 'Normal'}</Badge>
                </div>
                <p className="text-sm text-slate-600 line-clamp-3">{a.content}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                  <span>{formatRelativeTime(a.created_at)}</span>
                  {a.created_by_name && <span>- {a.created_by_name}</span>}
                  {a.expires_at && <span>Son: {formatDate(a.expires_at)}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Announcement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Yeni Duyuru</DialogTitle>
            <DialogDescription>{meclis?.name} icin duyuru yayinlayin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Baslik *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Duyuru basligi" data-testid="announcement-title-input" /></div>
            <div><Label>Icerik *</Label><Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Duyuru icerigi" rows={5} data-testid="announcement-content-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Oncelik</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="yuksek">Yuksek</SelectItem>
                    <SelectItem value="acil">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Son Gosterim Tarihi</Label><Input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} /></div>
            </div>
            <div>
              <Label>Hedef Kitle</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {TARGET_AUDIENCES.map(ta => (
                  <label key={ta.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.target_audience.includes(ta.value)}
                      onCheckedChange={checked => {
                        setForm(f => ({
                          ...f,
                          target_audience: checked
                            ? [...f.target_audience, ta.value]
                            : f.target_audience.filter(v => v !== ta.value)
                        }));
                      }}
                    />
                    {ta.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Iptal</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="submit-announcement-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Megaphone className="w-4 h-4 mr-2" />} Yayinla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ REPORTS TAB ============
const ReportsTab = () => {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadReports(); }, []);
  const loadReports = useCallback(async () => {
    try { const r = await meclisBaskaniAPI.getReports(); setReports(r.data); }
    catch { toast.error('Raporlar yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  if (!reports) return null;

  const statusColors = {
    yeni: '#6366f1', inceleniyor: '#3b82f6', islemde: '#f59e0b', cozuldu: '#10b981',
    kapatildi: '#64748b', beklemede: '#eab308', iptal: '#ef4444',
  };

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <h3 className="text-lg font-semibold text-slate-900">{reports.meclis_name} - Raporlar</h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Toplam Talep" value={reports.total_tickets} icon={FileText} color="blue" />
        <StatCard title="Cozulen" value={reports.resolved_tickets} icon={CheckCircle} color="green" />
        <StatCard title="Cozum Orani" value={`%${reports.resolution_rate}`} icon={TrendingUp} color="green" />
        <StatCard title="Toplantilar" value={reports.total_meetings} icon={Calendar} color="cyan" />
      </div>

      {/* Tickets by Status */}
      <Card className="card-shadow">
        <CardHeader><CardTitle className="text-base">Talep Durum Dagilimi</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(reports.tickets_by_status).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Veri yok</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(reports.tickets_by_status).map(([status, count]) => {
                const pct = reports.total_tickets > 0 ? (count / reports.total_tickets * 100) : 0;
                return (
                  <div key={status} className="flex items-center gap-3">
                    <span className="w-32 text-sm text-slate-600">{TICKET_STATUS[status]?.label || status}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                      <div className="h-full rounded-full flex items-center justify-end pr-2 text-xs font-semibold text-white transition-all duration-700 ease-out" style={{ width: `${Math.max(pct, pct > 0 ? 12 : 0)}%`, backgroundColor: statusColors[status] || '#94a3b8' }}>
                        {count}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400 w-12 text-right">%{pct.toFixed(0)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tickets by Category */}
      {Object.keys(reports.tickets_by_category).length > 0 && (
        <Card className="card-shadow">
          <CardHeader><CardTitle className="text-base">Kategori Dagilimi</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(reports.tickets_by_category).map(([cat, count]) => (
                <div key={cat} className="bg-slate-50 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors">
                  <p className="text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-xs text-slate-500 capitalize mt-1">{cat}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Working Group Participation */}
      {reports.wg_participation?.length > 0 && (
        <Card className="card-shadow">
          <CardHeader><CardTitle className="text-base">Calisma Grubu Katilim</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reports.wg_participation.map((wg, i) => (
                <div key={wg.name || `wg-${i}`} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm text-slate-700">{wg.name}</span>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-semibold text-slate-900">{wg.members} uye</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ MAIN DASHBOARD ============
const MeclisBaskaniDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Meclis Baskani Paneli');
  const [activeTab, setActiveTab] = useState('home');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);
  const loadDashboard = useCallback(async () => {
    try { const r = await meclisBaskaniAPI.getDashboard(); setDashboardData(r.data); }
    catch (e) { console.error('Dashboard error:', e); }
    finally { setLoading(false); }
  }, []);

  return (
    <div className="space-y-6" data-testid="meclis-baskani-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Meclis Baskani Paneli</h1>
          <p className="text-sm text-slate-500 mt-0.5">Hosgeldiniz, {user?.name} {user?.surname}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-3 md:grid-cols-6 h-auto gap-1 bg-slate-100/80 p-1.5 rounded-xl" data-testid="mb-tabs">
          <TabsTrigger value="home" className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-home">
            <LayoutDashboard className="w-4 h-4 mr-1.5 hidden sm:block" /> Ana Sayfa
          </TabsTrigger>
          <TabsTrigger value="groups" className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-groups">
            <UsersRound className="w-4 h-4 mr-1.5 hidden sm:block" /> Calisma Gruplari
          </TabsTrigger>
          <TabsTrigger value="tickets" className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-tickets">
            <FileText className="w-4 h-4 mr-1.5 hidden sm:block" /> Talepler
          </TabsTrigger>
          <TabsTrigger value="meetings" className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-meetings">
            <Calendar className="w-4 h-4 mr-1.5 hidden sm:block" /> Toplantilar
          </TabsTrigger>
          <TabsTrigger value="announcements" className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-announcements">
            <Megaphone className="w-4 h-4 mr-1.5 hidden sm:block" /> Duyurular
          </TabsTrigger>
          <TabsTrigger value="reports" className="text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-reports">
            <BarChart3 className="w-4 h-4 mr-1.5 hidden sm:block" /> Raporlar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="home" className="mt-6">
          <HomeTab data={dashboardData} loading={loading} />
        </TabsContent>
        <TabsContent value="groups" className="mt-6">
          <WorkingGroupsTab meclis={dashboardData?.meclis} />
        </TabsContent>
        <TabsContent value="tickets" className="mt-6">
          <TicketsTab meclis={dashboardData?.meclis} />
        </TabsContent>
        <TabsContent value="meetings" className="mt-6">
          <MeetingsTab meclis={dashboardData?.meclis} />
        </TabsContent>
        <TabsContent value="announcements" className="mt-6">
          <AnnouncementsTab meclis={dashboardData?.meclis} />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <ReportsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MeclisBaskaniDashboard;
